import { useState } from 'react';

import type {
  HitlActionRequest,
  HitlActionType,
  HitlItem,
  MultiDateSplitCandidate,
} from '../services/types';

import { Button } from './Button';

export interface MultiDateSplitCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

/**
 * v0.6 Stage H.2 — UI card for the ``multi_date_split`` HITL.
 *
 * Fired by ``agents/ras.py`` when ``_classify_values`` reports
 * ``multi_date`` C ≥ ``MULTI_DATE_THRESHOLD`` (0.60) on a column —
 * i.e. ≥ 60% of sampled cells contain two parseable dates separated
 * by a recognised separator (``2024-01-01 to 2024-01-31``,
 * ``15/03/2024 - 22/03/2024`` …).
 *
 * Always-recommended-tier per dev plan §3.8.2: the detector is
 * high-precision and the action is destructive (drops the source
 * column), so the standard composite-confidence banding is bypassed.
 * The reviewer sees a single proposal and either confirms (with
 * optional output-name edits) or skips.
 *
 * Reviewer dispatches ``choose_multi_date_split``. Empty payload
 * ``{}`` signals confirmed-with-defaults; fields matching the spec
 * defaults are omitted so the wire payload is the minimal delta
 * (so the carry-forward log doesn't record redundant rename-to-self
 * entries). Backend writes a ``column_policy`` audit row keyed on
 * ``(file_hash, column)``; on the next pipeline tick CPA → MOA's
 * ``_split_multi_date_columns`` stage applies the split, dropping
 * the source column and inserting the two siblings at the original
 * index.
 */
export function MultiDateSplitCard({
  hit,
  onAction,
  reviewerId,
}: MultiDateSplitCardProps) {
  const candidates = hit.candidates as
    | Array<MultiDateSplitCandidate>
    | null
    | undefined;
  const candidate =
    candidates && candidates.length > 0 ? candidates[0] : null;

  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [startName, setStartName] = useState(
    candidate?.start_column_name ?? '',
  );
  const [endName, setEndName] = useState(
    candidate?.end_column_name ?? '',
  );

  const onConfirm = async () => {
    if (!candidate) return;
    const payload: Record<string, string> = {};
    const trimmedStart = startName.trim();
    const trimmedEnd = endName.trim();
    if (
      trimmedStart.length > 0 &&
      trimmedStart !== candidate.start_column_name
    ) {
      payload.start_column_name = trimmedStart;
    }
    if (
      trimmedEnd.length > 0 &&
      trimmedEnd !== candidate.end_column_name
    ) {
      payload.end_column_name = trimmedEnd;
    }
    setPending('choose_multi_date_split');
    setError(null);
    try {
      await onAction(hit.hit_id, {
        type: 'choose_multi_date_split',
        payload,
        reviewer_id: reviewerId,
      });
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  const trimmedStart = startName.trim();
  const trimmedEnd = endName.trim();
  // Confirm gates on both names being non-empty and different from
  // each other — sending a payload with two identical sibling names
  // would create a downstream column collision.
  const namesValid =
    trimmedStart.length > 0 &&
    trimmedEnd.length > 0 &&
    trimmedStart !== trimmedEnd;
  // ``hitl_adapter._sample_rows`` reshapes the agent's
  // ``list[list[str]]`` payload into ``HitlSampleRow`` objects with
  // the source-cell snippet on ``.original``. We only want the
  // first three for the preview.
  const sampleRows = (hit.sample_rows ?? []).slice(0, 3);

  return (
    <li
      data-rule-card="multi-date-split"
      data-resolution-type="multi_date_split"
      className={`rounded-md border bg-paper border-l-4 border-l-brand border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="m-0 text-sm font-semibold flex-1">
            <code>{hit.column}</code> · split date range column
          </h3>
          <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-surface-alt text-muted whitespace-nowrap">
            multi-date split
          </span>
        </div>
        {candidate && (
          <p className="text-xs text-muted m-0">
            <strong className="text-ink">
              {candidate.parsed_count} of {candidate.sample_count}
            </strong>{' '}
            sampled cells contain two dates separated by{' '}
            <code className="font-mono text-[11px]">
              {candidate.separator_pattern}
            </code>
            . Date format: <code className="font-mono text-[11px]">{candidate.date_format}</code>.
          </p>
        )}
        {sampleRows.length > 0 && (
          <ul className="text-[11px] text-muted m-0 pl-4 list-disc space-y-0.5">
            {sampleRows.map((row, i) => (
              <li key={i} className="font-mono">
                {String(row.original ?? '')}
              </li>
            ))}
          </ul>
        )}
      </div>

      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wide text-muted">
              Output column names
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-xs text-muted">
                Start
                <input
                  type="text"
                  value={startName}
                  onChange={(e) => setStartName(e.target.value)}
                  className="rounded border border-border bg-paper px-2 py-1 text-sm font-mono text-ink"
                  data-testid="multi-date-start-input"
                  spellCheck={false}
                  autoComplete="off"
                  disabled={pending !== null}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted">
                End
                <input
                  type="text"
                  value={endName}
                  onChange={(e) => setEndName(e.target.value)}
                  className="rounded border border-border bg-paper px-2 py-1 text-sm font-mono text-ink"
                  data-testid="multi-date-end-input"
                  spellCheck={false}
                  autoComplete="off"
                  disabled={pending !== null}
                />
              </label>
            </div>
            {!namesValid && (
              <p className="text-[11px] text-warn m-0">
                Both names are required and must differ.
              </p>
            )}
          </fieldset>

          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={pending !== null || !namesValid}
              data-testid="multi-date-split-confirm"
            >
              {pending === 'choose_multi_date_split' ? 'Saving…' : 'Confirm split'}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
